<script>
    import * as pdfjsLib from "pdfjs-dist";

    pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdfjs/pdf.worker.min.mjs`;
    import ToolbarBackButton from "./_components/ToolbarBackButton.svelte";
    import { DocumentService } from "$/services/DocumentService.js";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { redirect } from "@roxi/routify";
    import { getEffectivePricingPlan } from "$/utils/Utils";
    import { TenantStore } from "$/stores/TenantStore";
    import { transliterate } from "transliteration";
    import { getURLFileName } from "$/utils/upload";
    import RecentDocuments from "./RecentDocuments/RecentDocuments.svelte";

    if (!$TenantStore?.active) TenantStore.init();

    $: effectivePricingPlan = $TenantStore?.active
        ? getEffectivePricingPlan($TenantStore?.active)
        : null;
    $: pdfUrl =
        inputValue != "" ? `/pdf/${encodeURIComponent(inputValue)}/` : null;

    const PDF_SIZE_LIMIT_FREE = 2;
    const PDF_SIZE_LIMIT_PRO = 30;

    let inputValue = "";
    let uploadProgress = 0;
    let notValidUrl = true;
    let isLocalFilePath = true;
    let loadingPDFUrl = false;
    let pdfUploading = false;
    let pdfTooBig = false;
    let pdfTooBigForFreeUsers = false;

    $: hasUrlError = (notValidUrl || isLocalFilePath) && inputValue !== "";

    const onProgress = (percentage) => {
        uploadProgress = Math.round(percentage);
    };

    async function checkIsValidUrl() {
        if (inputValue === "") {
            notValidUrl = true;
            return;
        }

        (async () => {
            const { isPdf, isLocal } = await isValidUrl(inputValue);
            notValidUrl = !isPdf;
            isLocalFilePath = isLocal;
        })();
    }

    async function isValidUrl(url) {
        let isPdf = false;
        loadingPDFUrl = true;
        // if it doesn't end on .pdf or .pdf/ or .pdf? or .pdf# or .pdf& or .pdf=
        if (!url.match(/\.pdf($|\/|\?|\#|\&|\=)/)) {
            try {
                await pdfjsLib
                    .getDocument(`${ServiceConsts.CLOUD_FLARE_WORKER}${url}`)
                    .promise.then(
                        (pdf) => (isPdf = true),
                        (error) => (isPdf = false),
                    );
            } catch (e) {
                isPdf = false;
            }

            loadingPDFUrl = false;
            return { isPdf: isPdf, isLocal: false };
        }

        // else if ends on .pdf or .pdf/ or .pdf? or .pdf# or .pdf& or .pdf=
        try {
            const localFilePathPattern =
                /^(?:[A-Z]:\\|\/Users\/|\/Volumes\/|\/home\/|file:\/\/\/|~\/|\.\/|\.\.\/)/i;
            if (localFilePathPattern.test(url)) {
                loadingPDFUrl = false;
                isPdf = true;
                return { isPdf: isPdf, isLocal: true };
            }
            new URL(url);
            isPdf = true;
        } catch (e) {
            isPdf = false;
        }

        loadingPDFUrl = false;
        return { isPdf: isPdf, isLocal: false };
    }

    async function handlePDFUpload(event, onProgress) {
        pdfTooBig = false;
        pdfTooBigForFreeUsers = false;
        pdfUploading = true;
        let file = event.target.files[0];

        if (!file) {
            pdfUploading = false;
            return;
        }

        let data;

        // Check if file name contains non-Latin characters
        const nonLatinPattern = /[^\u0000-\u007F]/;
        if (nonLatinPattern.test(file.name)) {
            const latinFileName = transliterate(file.name);
            file = new File([file], latinFileName, { type: file.type });
        }

        if (
            effectivePricingPlan < 1 &&
            file.size > PDF_SIZE_LIMIT_FREE * 1024 * 1024
        ) {
            pdfUploading = false;
            pdfTooBigForFreeUsers = true;
            return;
        }

        if (file.size > PDF_SIZE_LIMIT_PRO * 1024 * 1024) {
            pdfTooBig = true;
            pdfUploading = false;
            return;
        }

        try {
            data = await DocumentService.uploadPrivatePDF(file, $TenantStore.active.id, onProgress);

            let fullUrl = "";
            if (data?.id) fullUrl = `/pdf/${data.id}/`;

            window.open(fullUrl, "_self");
        } catch (e) {
            pdfTooBig = true;
        }

        pdfUploading = false;
    }

    const openPDFURL = async () => {
        // Open a blank window immediately to avoid popup blocking
        const newWindow = window.open("", "_blank");
        let url;
        let pdfExist = await DocumentService.checkPDFExistence(inputValue);

        if (!pdfExist) {
            url = pdfUrl;
        } else {
            const fullUrl =
                ServiceConsts.CDN_PUBLIC_URL +
                "pdf/web/" +
                btoa(inputValue) +
                "/" +
                getURLFileName(inputValue);

            url = `${ServiceConsts.DASHBOARD_URL}/pdf/${encodeURIComponent(fullUrl)}/`;
        }

        newWindow.location.href = url;
    };
</script>

<div class="toolbar">
    <ToolbarBackButton showText={true}></ToolbarBackButton>
</div>
<div class="pdf-page">
    <div class="header">
        <svg
            class="header__icon"
            stroke="currentColor"
            viewBox="0 0 90 90"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M33.9719 41.1031C34.7811 41.1031 35.4787 41.3413 36.0647 41.8176C36.6506 42.2659 36.9436 42.8544 36.9436 43.5829C36.9436 43.9472 36.846 44.4095 36.6506 44.9699C36.4832 45.5303 36.26 46.0347 35.981 46.483C34.9764 48.1923 33.7068 49.4532 32.1721 50.2658C30.6375 51.0784 28.74 51.4846 26.4798 51.4846C23.8848 51.4846 21.6246 50.8542 19.6993 49.5933C17.774 48.3324 16.2951 46.5391 15.2626 44.2134C14.2581 41.8877 13.7559 39.1837 13.7559 36.1015C13.7559 33.0472 14.2581 30.3993 15.2626 28.1577C16.2672 25.9161 17.7182 24.2068 19.6156 23.03C21.5409 21.8251 23.843 21.2226 26.5217 21.2226C28.4749 21.2226 30.2468 21.5449 31.8373 22.1893C33.4278 22.8058 34.6695 23.6744 35.5624 24.7952C36.4832 25.888 36.9436 27.1209 36.9436 28.4939C36.9436 29.7268 36.5669 30.7356 35.8135 31.5201C35.0881 32.3047 34.1393 32.697 32.9674 32.697C31.7675 32.697 30.8049 32.3748 30.0794 31.7303C29.3539 31.0578 28.9911 30.1752 28.9911 29.0824C28.9911 28.4939 29.1028 27.9335 29.326 27.4011C28.7121 27.0089 27.8192 26.8127 26.6473 26.8127C24.4987 26.8127 22.9501 27.5552 22.0013 29.0403C21.0526 30.4974 20.5783 32.8091 20.5783 35.9754C20.5783 39.2818 21.0945 41.7616 22.1269 43.4148C23.1593 45.068 24.7638 45.8946 26.9402 45.8946C27.9727 45.8946 28.8377 45.6704 29.5353 45.2221C30.2329 44.7457 30.8328 44.0172 31.335 43.0365C31.6978 42.28 32.0605 41.7756 32.4233 41.5234C32.8139 41.2432 33.3301 41.1031 33.9719 41.1031Z"
            />
            <path
                d="M77.3862 35.3711C77.9982 35.3711 78.485 35.5235 78.8466 35.8281C79.2082 36.1051 79.3891 36.5621 79.3891 37.1992V39.0273C79.3891 39.6643 79.2082 40.1352 78.8466 40.4399C78.485 40.7445 77.9982 40.8969 77.3862 40.8969H76.7604L70.8771 62.9999C70.7658 63.4431 70.5155 63.8032 70.126 64.0802C69.7366 64.3294 69.3054 64.4541 68.8325 64.4541H65.578C65.0773 64.4541 64.6322 64.3294 64.2427 64.0802C63.8811 63.8032 63.6308 63.4431 63.4917 62.9999L58.7767 48.1676L54.0618 62.9999C53.9227 63.4431 53.6584 63.8032 53.269 64.0802C52.8795 64.3294 52.4345 64.4541 51.9338 64.4541H48.6375C48.1646 64.4541 47.7334 64.3294 47.344 64.0802C46.9545 63.8032 46.7042 63.4431 46.5929 62.9999L40.7097 40.8969H40.0838C39.4996 40.8969 39.0267 40.7445 38.6651 40.4399C38.3035 40.1352 38.1227 39.6643 38.1227 39.0273V37.1992C38.1227 36.5621 38.3035 36.1051 38.6651 35.8281C39.0267 35.5235 39.4996 35.3711 40.0838 35.3711H48.1368C48.7209 35.3711 49.1938 35.5235 49.5554 35.8281C49.917 36.1051 50.0979 36.5621 50.0979 37.1992V39.0273C50.0979 39.6643 49.917 40.1352 49.5554 40.4399C49.1938 40.7445 48.7209 40.8969 48.1368 40.8969H47.5109L51.141 55.106L55.6473 41.0215C55.7864 40.5784 56.0229 40.2321 56.3567 39.9828C56.6905 39.7059 57.0799 39.5674 57.525 39.5674H61.2385C61.6558 39.5674 62.0313 39.7059 62.3651 39.9828C62.6989 40.2321 62.9354 40.5784 63.0744 41.0215L67.5391 54.9398L71.1274 40.8969H70.5016C69.8896 40.8969 69.4028 40.7445 69.0412 40.4399C68.6795 40.1352 68.4987 39.6643 68.4987 39.0273V37.1992C68.4987 36.5621 68.6795 36.1051 69.0412 35.8281C69.4028 35.5235 69.8896 35.3711 70.5016 35.3711H77.3862Z"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M39.6223 21.2995C38.5944 21.4756 37.573 20.9557 37.1105 20.021L36.9292 19.6545C36.5997 18.9883 36.6034 18.2058 36.9394 17.5429C37.2754 16.8799 37.9042 16.4142 38.6363 16.286C40.7566 15.915 42.9511 15.7205 45.1969 15.7205C55.6407 15.7205 65.021 19.9327 70.9483 26.5968C71.5317 27.2528 71.7024 28.1784 71.3915 28.9993C71.0805 29.8202 70.3393 30.4004 69.4678 30.5052L68.9891 30.5628C68.2309 30.6539 67.4755 30.3722 66.9621 29.8069C62.0739 24.4247 54.2151 20.8296 45.1969 20.8296C43.2822 20.8296 41.418 20.9919 39.6223 21.2995Z"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M29.0445 64.5747C29.3227 63.4595 30.3645 62.7073 31.5106 62.7942L32.7521 62.8882C32.9886 62.9061 33.2209 62.9596 33.4414 63.0468C35.2503 63.7624 37.1744 64.3192 39.188 64.6927C39.3665 64.7258 39.5405 64.7794 39.7067 64.8525L41.1437 65.484C42.222 65.9579 42.7852 67.1548 42.4631 68.2877C42.141 69.4206 41.0323 70.1421 39.866 69.9778C36.5065 69.5046 33.3168 68.5909 30.3852 67.305C29.3326 66.8433 28.7663 65.6899 29.0445 64.5747Z"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M20.9882 55.8732C22.2544 56.1778 23.034 57.4512 22.7294 58.7174C21.9399 61.9996 21.1008 64.5684 20.4944 66.4251C20.431 66.6191 20.3702 66.8052 20.3123 66.9837C21.3605 66.5797 22.6163 66.059 24.175 65.4128C25.8212 64.7302 27.8053 63.9076 30.2399 62.9343C31.4491 62.4508 32.8214 63.0392 33.3048 64.2485C33.7883 65.4577 33.1999 66.83 31.9906 67.3134C29.5657 68.2829 27.4343 69.1712 25.6589 69.9111C24.1522 70.539 22.9018 71.0601 21.9458 71.4334C20.8708 71.8531 19.8941 72.1998 19.0863 72.3636C18.6821 72.4456 18.1713 72.5146 17.635 72.4551C17.0658 72.392 16.3274 72.1641 15.7349 71.5229C15.161 70.9018 14.9928 70.1813 14.9493 69.6781C14.9066 69.1839 14.9663 68.7104 15.0354 68.3331C15.0683 68.1534 15.1095 67.9647 15.1574 67.7688C15.3096 67.1255 15.537 66.4014 15.7763 65.6612C15.8594 65.4044 15.9471 65.1361 16.0388 64.8556C16.6409 63.0141 17.4137 60.6505 18.144 57.6144C18.4486 56.3482 19.722 55.5686 20.9882 55.8732Z"
            />
        </svg>

        <h1 class="header__text">PDF Highlighter</h1>
    </div>

    <div class="pdf-url-input-wrapper">
        <input
            bind:value={inputValue}
            on:input={checkIsValidUrl}
            class="pdf-url-input"
            type="text"
            placeholder="Paste PDF URL"
            class:pdf-url-input--error={hasUrlError}
        />

        {#if hasUrlError}
            <span class="pdf-url-input__error-message">
                {#if notValidUrl}
                    Please enter a valid URL <br /> (e.g. https://example.com/file.pdf)
                {:else if isLocalFilePath}
                    Opening local files directly is not supported. <br /> Please
                    upload the file first.
                {/if}
            </span>
        {/if}
    </div>

    <button
        class="open-button"
        on:click={openPDFURL}
        class:open-button--disabled={notValidUrl ||
            isLocalFilePath ||
            inputValue === "" ||
            loadingPDFUrl}
    >
        {#if loadingPDFUrl}
            <div class="open-button__loading">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="h-6 w-6"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                </svg>
            </div>
            <span class="open-button__loading--text">Checking URL...</span>
        {:else}
            <svg
                class="open-button__icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                />
            </svg>

            Open from URL
        {/if}
    </button>

    <label
        class={pdfUploading
            ? "open-button open-button--disabled"
            : "open-button"}
        for="pdf-upload"
    >
        {#if !pdfUploading}
            <svg
                class="open-button__icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
            </svg>
            Upload PDF
        {:else if pdfUploading}
            <div class="open-button__loading">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="h-6 w-6"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                </svg>
            </div>
            <span class="open-button__loading--text"
                >Uploading... {uploadProgress}%</span
            >
            <span
                class="open-button__loading--progress"
                style="width: {uploadProgress}%"
            ></span>
        {/if}
    </label>
    <input
        class="hidden"
        id="pdf-upload"
        on:change={(e) => {
            handlePDFUpload(e, onProgress);
        }}
        type="file"
        accept="application/pdf"
    />

    <RecentDocuments />

    {#if pdfTooBigForFreeUsers}
        <div class="banner pdf-too-big-for-free-users-info">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
            </svg>
            <div>
                <span>File size bigger than 2MB.</span>
                <button on:click={() => $redirect("/payment")}
                    >Upgrade to increase limit to 30MB!
                </button>
            </div>
        </div>
    {/if}

    {#if pdfTooBig}
        <div class="banner pdf-too-big-info">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
            </svg>
            <span>File too big. Max size is 30MB.</span>
        </div>
    {/if}
</div>

<style>
    @import "../../main.css";

    .toolbar {
        @apply absolute left-0 top-0 flex min-h-[2.5rem] items-center px-4 py-1;
    }

    .pdf-page {
        @apply flex h-screen w-full flex-col items-center justify-center gap-4 p-8;
    }

    .header {
        @apply flex select-none items-center justify-center gap-2;
    }

    .header__text {
        @apply text-2xl font-medium;
    }

    .header__beta {
        @apply mt-2 self-start text-xs font-medium leading-none;
    }

    .header__icon {
        @apply h-12 w-12 fill-black text-black;
    }

    .pdf-url-input-wrapper {
        @apply flex w-full max-w-xs flex-col !important;
    }

    .pdf-url-input {
        @apply reset h-12 w-full max-w-xs rounded-full border border-solid border-black bg-white px-4 py-2 text-lg text-black placeholder:text-gray-400 !important;
    }

    .pdf-url-input--error {
        @apply border-pink-600 !important;
    }

    .pdf-url-input__error-message {
        @apply w-full text-center text-xs text-pink-600 !important;
    }

    .open-button {
        @apply reset relative flex h-12 min-h-12 w-full max-w-xs cursor-pointer select-none items-center justify-center rounded-full bg-pink-500 px-4 py-2 text-center text-lg text-white hover:bg-pink-600 !important;
    }

    .open-button--disabled {
        @apply pointer-events-none cursor-not-allowed bg-neutral-200 text-gray-400 shadow-sm transition-all hover:scale-105 hover:bg-neutral-300 hover:text-gray-500 hover:shadow-md !important;
    }

    .open-button__icon {
        @apply absolute left-4 top-1/2 h-6 w-6 -translate-y-1/2 transform !important;
    }

    .open-button__coming-soon {
        @apply absolute right-4 top-0 h-full !important;
    }

    .open-button__loading {
        @apply absolute left-4 top-3 z-20 flex h-6 w-6 animate-spin cursor-default select-none items-center justify-center text-white !important;
    }

    .open-button__loading--text {
        @apply relative z-20 text-white !important;
    }

    .open-button__loading--progress {
        @apply absolute left-0 top-0 z-10 h-full min-w-[3.5rem] rounded-full bg-pink-500;
        transition: width 0.2s; /* Add a smooth transition */
    }

    .placeholder-wrapper {
        @apply w-full max-w-xs !important;
    }

    .banner {
        @apply flex w-full max-w-xs gap-4 rounded bg-yellow-100 p-4 text-yellow-600;
    }

    .banner svg {
        @apply h-6 w-6 shrink-0;
    }

    .pdf-too-big-for-free-users-info {
        @apply bg-yellow-100 text-yellow-600;
    }

    .pdf-too-big-for-free-users-info div {
        @apply flex flex-col;
    }

    .pdf-too-big-for-free-users-info button {
        @apply text-start underline;
    }

    .pdf-too-big-info {
        @apply bg-pink-100 text-pink-500;
    }
</style>
